import { Tooltip } from "@mantine/core";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { translationStatusAtom } from "../atoms/translation-status-atom";

type Props = { pageId: string };

/**
 * Tiny indicator next to a page in the sidebar tree:
 *   - green  → all targets ready & up to date
 *   - amber  → at least one pending
 *   - orange → translated but outdated (page edited since)
 *   - red    → at least one failed
 *
 * Renders nothing when the space hasn't enabled translations or the
 * page has no translation rows yet. Read-only — uses a global atom
 * populated by the tree root, no extra network requests per row.
 */
export function TranslationDot({ pageId }: Props) {
  const { t } = useTranslation();
  const status = useAtomValue(translationStatusAtom);
  if (!status || !status.enabled) return null;
  const counts = status.perPage[pageId];
  if (!counts) return null;

  let color = "var(--mantine-color-gray-5)";
  let label = "";
  if (counts.failed > 0) {
    color = "var(--mantine-color-red-6)";
    label = t("{{n}} translation(s) failed", { n: counts.failed });
  } else if (counts.pending > 0) {
    color = "var(--mantine-color-yellow-6)";
    label = t("Translating… {{n}} pending", { n: counts.pending });
  } else if ((counts.stale ?? 0) > 0) {
    color = "var(--mantine-color-orange-6)";
    label = t("{{n}} translation(s) outdated — page changed since", {
      n: counts.stale,
    });
  } else if (counts.ready > 0) {
    color = "var(--mantine-color-green-6)";
    label = t("Translated to {{n}} language(s)", { n: counts.ready });
  } else {
    return null;
  }

  return (
    <Tooltip label={label} withArrow position="left" openDelay={300}>
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          marginRight: 4,
          flex: "0 0 auto",
        }}
      />
    </Tooltip>
  );
}
